import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { DashboardSkeleton } from '../../components/ui/LoadingState';
import { NotificationContainer } from '../../components/ui/NotificationToast';
import { MapPin, Plus } from 'lucide-react';
import EditLocationModal from './components/EditLocationModal';
import DeleteLocationConfirmationModal from './components/DeleteLocationConfirmationModal';
import { useData } from '../../context/DataContext';
import { formatDate } from '../../utils/formatters';
import { usePreferences } from '../../hooks/usePreferences';

const LocationManagement = () => {
  const { currentUser } = useData();
  const canDelete = currentUser?.role === 'admin' || currentUser?.role === 'system_admin';
  const [locations, setLocations] = useState([]);
  const [newLocationName, setNewLocationName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);

  const { preferences } = usePreferences();
  const preferredFormat = preferences?.date_format || 'DD/MM/YYYY';

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setIsLoading(true);
    const { data, error } = await supabase.from('locations').select('*').order('name');
    if (error) {
      addNotification('Error fetching locations', 'error');
    } else {
      setLocations(data);
    }
    setIsLoading(false);
  };

  const addNotification = (message, type = 'success') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLocationName.trim()) {
      addNotification('Location name cannot be empty', 'error');
      return;
    }

    const { data, error } = await supabase
      .from('locations')
      .insert([{ name: newLocationName.trim() }])
      .select();

    if (error) {
      addNotification(`Error: ${error.message}`, 'error');
    } else {
      addNotification(`Location "${data[0].name}" added successfully.`);
      setLocations([...locations, data[0]].sort((a, b) => a.name.localeCompare(b.name)));
      setNewLocationName('');
    }
  };

  if (isLoading) {
    return (
      <div>
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <MapPin className="text-primary" /> Location Management
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 bg-card p-6 rounded-lg border shadow-sm">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Plus size={18} /> Add New Location
          </h2>
          <form onSubmit={handleAddLocation} className="space-y-4">
            <div>
              <label htmlFor="locationName" className="block text-sm font-medium text-muted-foreground mb-1">
                Location Name
              </label>
              <Input
                id="locationName"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                placeholder="e.g., HQ - Floor 1"
                className="bg-card"
              />
            </div>
            <Button type="submit" className="w-full">Add Location</Button>
          </form>
        </div>

        <div className="md:col-span-2 bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-border">
            <h2 className="text-lg font-bold text-foreground">Existing Locations</h2>
          </div>
          <div className="overflow-auto max-h-[calc(100vh-250px)] relative">
            <table className="min-w-full divide-y divide-border/60 dark:divide-border/35">
              <thead className="bg-muted sticky top-0 z-10 shadow-sm">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Created At</th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border/50 dark:divide-border/30">
                {locations.map((loc) => (
                  <tr key={loc.id} className="hover:bg-muted/80 transition-colors group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-foreground">{loc.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDate(loc.created_at, preferredFormat)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-primary hover:bg-blue-50"
                          onClick={() => {
                            setEditingLocation(loc);
                            setShowEditLocationModal(true);
                          }}
                        >
                          Edit
                        </Button>
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setLocationToDelete(loc);
                              setShowDeleteConfirmModal(true);
                            }}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {locations.length === 0 && (
              <div className="text-center py-12">
                <MapPin className="mx-auto h-12 w-12 text-gray-200 mb-4" />
                <p className="text-sm font-medium text-muted-foreground/70">No locations found. Add your first location to get started.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <NotificationContainer notifications={notifications} onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} />

      {showEditLocationModal && editingLocation && (
        <EditLocationModal
          location={editingLocation}
          onClose={() => {
            setShowEditLocationModal(false);
            setEditingLocation(null);
          }}
          onLocationUpdated={() => {
            fetchLocations();
            addNotification('Location updated successfully!', 'success');
          }}
        />
      )}

      {showDeleteConfirmModal && locationToDelete && (
        <DeleteLocationConfirmationModal
          location={locationToDelete}
          onClose={() => {
            setShowDeleteConfirmModal(false);
            setLocationToDelete(null);
          }}
          onConfirmDelete={() => {
            fetchLocations();
            addNotification('Location deleted successfully!', 'success');
          }}
        />
      )}
    </div>
  );
};

export default LocationManagement;
